import React, { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { GetAllPublicApartmentUnits, UpdatePublicApartmentUnit, PublicApartmentUnit, PublicApartmentOccupant, DeletePublicApartmentUnit } from "./DataServices/PublicApartmentUnitsService";
import { BuildingOfficeIcon, UserGroupIcon, HomeIcon, MagnifyingGlassIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { saveListing, saveRentedListing } from "./DataServices/ApartmentsListingsDataServices";
import { baseUrl, getTokenFromLocalStorage } from "./DataServices/Configuration";
import Notification from "./Notification";

const ExtractedApartments: React.FC = () => {
  const [apartments, setApartments] = useState<PublicApartmentUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState<'listing' | 'moveout'>('listing');
  const [actionStep, setActionStep] = useState<1 | 2>(1);
  const [selectedApt, setSelectedApt] = useState<PublicApartmentUnit | null>(null);

  const [isVacant, setIsVacant] = useState(false);
  const [moveInDate, setMoveInDate] = useState("");
  const [moveOutDate, setMoveOutDate] = useState("");
  const [saving, setSaving] = useState(false);

  const [editingOccupants, setEditingOccupants] = useState<Partial<PublicApartmentOccupant>[]>([]);

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationMessageType, setNotificationMessageType] = useState("success");

  const filteredApartments = apartments.filter(apt => {
    const searchLower = searchTerm.toLowerCase();
    const titleMatch = apt.apartmentTitle?.toLowerCase().includes(searchLower);
    const buildingMatch = apt.building?.toLowerCase().includes(searchLower);
    const unitMatch = apt.unitName?.toLowerCase().includes(searchLower);
    const tenantMatch = apt.occupants.some(occ => occ.name?.toLowerCase().includes(searchLower) || occ.email?.toLowerCase().includes(searchLower));
    return titleMatch || buildingMatch || unitMatch || tenantMatch;
  });

  const fetchData = async () => {
    try {
      const data = await GetAllPublicApartmentUnits();
      setApartments(data);
    } catch (error) {
      console.error("Error loading extracted apartments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveTenants = async (listingId: string, occupants: any[]) => {
    const token = await getTokenFromLocalStorage();
    for (const occ of occupants) {
      const payload = {
        id: crypto.randomUUID(),
        listingId: listingId,
        name: occ.name,
        email: occ.email || "",
        phone: occ.phone || "",
        isGuarantor: occ.isCosigner,
        isReceiver: false,
        showingAppointmentStarted: false,
      };
      await fetch(`${baseUrl}/api/Appointments/TenantContact`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
    }
  };

  const hasMissingOccupantInfo = (occupants: PublicApartmentOccupant[]) =>
    occupants.some(occ => !occ.name?.trim() || !occ.email?.trim() || !occ.phone?.trim());

  const hasMissingMandatoryMoveOutInfo = (occupants: PublicApartmentOccupant[]) =>
    occupants.some(occ => !occ.name?.trim() || !occ.email?.trim());

  const initiateMoveToListing = (apt: PublicApartmentUnit) => {
    setSelectedApt(apt);
    setIsVacant(false);
    setMoveInDate("");
    setActionType('listing');
    if (hasMissingOccupantInfo(apt.occupants)) {
      setEditingOccupants(apt.occupants.map(occ => ({ ...occ })));
      setActionStep(1);
    } else {
      setActionStep(2);
    }
    setActionModalOpen(true);
  };

  const initiateMoveToMoveOut = (apt: PublicApartmentUnit) => {
    setSelectedApt(apt);
    setMoveOutDate("");
    setActionType('moveout');
    if (hasMissingOccupantInfo(apt.occupants)) {
      setEditingOccupants(apt.occupants.map(occ => ({ ...occ })));
      setActionStep(1);
    } else {
      setActionStep(2);
    }
    setActionModalOpen(true);
  };

  const handleEditingOccupantChange = (index: number, field: keyof PublicApartmentOccupant, value: string) => {
    const updated = [...editingOccupants];
    updated[index] = { ...updated[index], [field]: value };
    setEditingOccupants(updated);
  };

  const handleProceedFromMissingTenantInfo = async () => {
    const mandatoryMissing = hasMissingMandatoryMoveOutInfo(editingOccupants as PublicApartmentOccupant[]);
    if (mandatoryMissing && actionType === 'moveout') {
      setNotificationMessage("Please fill in occupant's required fields before proceeding.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
      return;
    }

    if (!selectedApt) return;

    setSaving(true);
    try {
      await UpdatePublicApartmentUnit(selectedApt.id, {
        ...selectedApt,
        occupants: editingOccupants,
      });

      const updatedApt = { ...selectedApt, occupants: editingOccupants as PublicApartmentOccupant[] };
      setSelectedApt(updatedApt);
      setApartments(prev => prev.map(a => a.id === updatedApt.id ? updatedApt : a));
      
      setActionStep(2);
    } catch (e) {
      console.error(e);
      setNotificationMessage("Failed to save tenant details. Please try again.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const submitToListing = async () => {
    if (!selectedApt) return;
    if (!isVacant && !moveInDate) {
      setNotificationMessage("Please provide either Is Vacant or Move In Date.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
      return;
    }
    
    setSaving(true);
    try {
      const listingId = crypto.randomUUID();
      const payload = [{
        id: listingId,
        idApartment: crypto.randomUUID(),
        publicApartmentUnitId: selectedApt.id,
        title: selectedApt.apartmentTitle,
        zillowTitle: selectedApt.zillowTitle || selectedApt.apartmentTitle,
        description: selectedApt.description || "",
        building: selectedApt.building,
        line: selectedApt.line || "",
        floor: selectedApt.floor || 0,
        rent: selectedApt.rentalPrice || 0,
        currentRent: selectedApt.currentRent || 0,
        numOfBedrooms: selectedApt.bedrooms || 0,
        isVacant: isVacant,
        moveInDate: moveInDate || null,
        accessCode: selectedApt.accessCode || "",
        appfolioLink: selectedApt.appfolioLink || "",
        youTubeLink: selectedApt.youTubeUrl || ""
      }];

      await saveListing(payload as any);
      await saveTenants(listingId, selectedApt.occupants);

      setActionModalOpen(false);
      
      setNotificationMessage("Successfully moved to Listings.");
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);

      await fetchData();
    } catch (e) {
      console.error(e);
      setNotificationMessage("Failed to move to listings.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const submitToMoveOut = async () => {
    if (!selectedApt) return;
    if (!moveOutDate) {
      setNotificationMessage("Please provide Move Out Date.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
      return;
    }
    if (hasMissingMandatoryMoveOutInfo(selectedApt.occupants)) {
      setNotificationMessage("All occupants must have their required details filled in before proceeding to move-out.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 4000);
      return;
    }
    
    setSaving(true);
    try {
      const listingId = crypto.randomUUID();
      const payload = [{
        id: listingId,
        idApartment: crypto.randomUUID(),
        title: selectedApt.apartmentTitle,
        zillowTitle: selectedApt.zillowTitle || selectedApt.apartmentTitle,
        description: selectedApt.description || "",
        building: selectedApt.building,
        line: selectedApt.line || "",
        floor: selectedApt.floor || 0,
        rent: selectedApt.rentalPrice || 0,
        currentRent: selectedApt.currentRent || 0,
        numOfBedrooms: selectedApt.bedrooms || 0,
        publicApartmentUnitId: selectedApt.id,
        isVacant: false,
        moveOutDate: moveOutDate,
        accessCode: selectedApt.accessCode || "",
        appfolioLink: selectedApt.appfolioLink || "",
        youTubeLink: selectedApt.youTubeUrl || "",
        occupants: selectedApt.occupants.map(occ => ({
          name: occ.name || "",
          email: occ.email || "",
          phone: occ.phone || ""
        }))
      }];

      await saveRentedListing(payload as any);
      await saveTenants(listingId, selectedApt.occupants);

      setActionModalOpen(false);
      
      setNotificationMessage("Successfully moved to Move-outs.");
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);

      await fetchData();
    } catch (e) {
      console.error(e);
      setNotificationMessage("Failed to move to move-outs.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <Notification
        isOpen={openNotification}
        message={notificationMessage}
        messageType={notificationMessageType}
        setIsOpen={setOpenNotification}
      />
      
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl tracking-tight">Extracted Data</h1>
          <p className="mt-2 text-sm text-gray-500">
            View all the recently extracted apartments and tenant information from external sources.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-3">
          <button
            onClick={() => navigate("/listing")}
            className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all duration-200"
          >
            <HomeIcon className="mr-2 h-5 w-5 text-gray-500" />
            Go to Listings
          </button>
          <button
            onClick={() => navigate("/rented-listing")}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-all duration-200"
          >
            <BuildingOfficeIcon className="mr-2 h-5 w-5 text-white" />
            Go to Move Outs
          </button>
        </div>
      </div>

      <div className="mb-6 max-w-md relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          type="text"
          className="block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          placeholder="Search by title, building, unit, or tenant..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filteredApartments.length === 0 ? (
        <div className="text-center rounded-xl border border-dashed border-gray-300 p-12 bg-gray-50">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No data found</h3>
          <p className="mt-1 text-sm text-gray-500">No extracted apartments have been processed yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredApartments.map((apt) => (
            <div
              key={apt.id}
              className="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-white shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="px-6 py-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                    {apt.building}
                  </span>
                  {apt.rentalPrice !== undefined && apt.rentalPrice !== null && (
                    <span className="text-lg font-bold text-gray-900">${apt.rentalPrice}</span>
                  )}
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                  {apt.apartmentTitle}
                </h3>
                
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  {apt.bedrooms !== undefined && apt.bedrooms !== null && (
                    <span>{apt.bedrooms} Bed</span>
                  )}
                  {apt.floor !== undefined && apt.floor !== null && (
                    <span>Floor {apt.floor}</span>
                  )}
                  {apt.line && (
                    <span>Line {apt.line}</span>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center text-sm font-medium text-gray-900 mb-3">
                    <UserGroupIcon className="mr-2 h-5 w-5 text-gray-400" />
                    Tenants & Guarantors ({apt.occupants.length})
                  </div>
                  <ul className="space-y-3">
                    {apt.occupants.slice(0, 3).map((occ) => (
                      <li key={occ.id} className="text-sm">
                        <div className="font-medium text-gray-800 flex items-center justify-between">
                          <span>{occ.name}</span>
                          {occ.isCosigner && (
                            <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                              Cosigner
                            </span>
                          )}
                        </div>
                        {occ.email && <div className="text-gray-500 truncate mt-0.5">{occ.email}</div>}
                      </li>
                    ))}
                    {apt.occupants.length > 3 && (
                      <li className="text-sm text-blue-600 font-medium">
                        + {apt.occupants.length - 3} more...
                      </li>
                    )}
                  </ul>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex gap-2">
                <button
                  onClick={() => navigate(`/extracted-apartments/${apt.id}`)}
                  className="w-full inline-flex justify-center items-center rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  View Details
                </button>
                <button
                  onClick={() => initiateMoveToListing(apt)}
                  className="w-full inline-flex justify-center items-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  To Listings
                </button>
                <button
                  onClick={() => initiateMoveToMoveOut(apt)}
                  className="w-full inline-flex justify-center items-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  To Move-outs
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unified Action Modal */}
      <Transition.Root show={actionModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setActionModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-t-2xl sm:rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full sm:my-8 sm:max-w-xl sm:p-6">
                  
                  {/* Step 1: Missing Tenant Information */}
                  {actionStep === 1 && (
                    <div>
                      {actionType === 'moveout' && selectedApt && hasMissingMandatoryMoveOutInfo(selectedApt.occupants) ? (
                        <div className="rounded-md bg-red-50 border border-red-200 p-4 mb-5">
                          <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <h3 className="text-sm font-semibold text-red-800">Missing Tenant Information</h3>
                              <p className="mt-1 text-sm text-red-700">
                                The following occupants have missing information. Fill in mandatory details to proceed.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-5">
                          <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <h3 className="text-sm font-semibold text-amber-800">Missing Tenant Information</h3>
                              <p className="mt-1 text-sm text-amber-700">
                                  The following occupants have missing information. You may proceed without them.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 mb-4">
                        Complete Tenant Details
                      </Dialog.Title>

                      <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
                        {editingOccupants.map((occ, idx) => {
                          const existingOccupant = selectedApt?.occupants[idx];
                          if (existingOccupant?.name?.trim() && existingOccupant?.email?.trim() && existingOccupant?.phone?.trim()) {
                            return null;
                          }

                          return (
                            <div key={idx} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-semibold text-gray-800">{occ.name || "Unknown"}</span>

                                {occ.isCosigner && (
                                  <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                                    Cosigner
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Name <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={occ.name || ""}
                                    onChange={(e) => handleEditingOccupantChange(idx, "name", e.target.value)}
                                    placeholder="Enter name"
                                    className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm ${!occ.name?.trim() ? 'ring-red-500' : 'ring-gray-300'}`}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Email <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="email"
                                    value={occ.email || ""}
                                    onChange={(e) => handleEditingOccupantChange(idx, "email", e.target.value)}
                                    placeholder="Enter email address"
                                    className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm ${!occ.email?.trim() ? 'ring-red-500' : 'ring-gray-300'}`}
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Phone <span className="text-gray-400 font-normal">(Optional)</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={occ.phone || ""}
                                    onChange={(e) => handleEditingOccupantChange(idx, "phone", e.target.value)}
                                    placeholder="Enter phone number"
                                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                                  />
                                </div>
                              </div>
                            </div>
                          )})}
                      </div>

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        <button
                          type="button"
                          disabled={saving || (actionType === 'moveout' && hasMissingMandatoryMoveOutInfo(editingOccupants as PublicApartmentOccupant[]))}
                          className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 disabled:opacity-50"
                          onClick={handleProceedFromMissingTenantInfo}
                        >
                          {saving ? "Saving..." : "Proceed"}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                          onClick={() => setActionModalOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2A: Listing Details */}
                  {actionStep === 2 && actionType === 'listing' && (
                    <div>
                      <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Move to Listings
                        </Dialog.Title>
                        {selectedApt && hasMissingOccupantInfo(selectedApt.occupants) && (
                          <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-left">
                            <p className="text-sm text-amber-800 flex items-start gap-2">
                              <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                              Some occupants have incomplete information. You may still proceed with listings.
                            </p>
                          </div>
                        )}
                        <div className="mt-4 text-left">
                          <label className="flex items-center gap-2 cursor-pointer mb-4">
                            <input
                              type="checkbox"
                              checked={isVacant}
                              onChange={(e) => setIsVacant(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                            />
                            <span className="text-sm font-medium text-gray-900">Is Vacant</span>
                          </label>
                          {!isVacant && (
                            <div className="mb-4">
                              <label className="block text-sm font-medium leading-6 text-gray-900">Move In Date</label>
                              <input
                                type="date"
                                value={moveInDate}
                                onChange={(e) => setMoveInDate(e.target.value)}
                                className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        <button
                          type="button"
                          disabled={saving}
                          className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 disabled:opacity-50"
                          onClick={submitToListing}
                        >
                          {saving ? "Saving..." : "Confirm"}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                          onClick={() => setActionModalOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2B: Move-out Details */}
                  {actionStep === 2 && actionType === 'moveout' && (
                    <div>
                      <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Move to Move-outs
                        </Dialog.Title>
                        {selectedApt && hasMissingMandatoryMoveOutInfo(selectedApt.occupants) ? (
                          <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-left">
                            <p className="text-sm text-red-800 flex items-start gap-2">
                              <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              All occupants must have their required details filled in. Move-out cannot proceed until complete.
                            </p>
                          </div>
                        ) : selectedApt && hasMissingOccupantInfo(selectedApt.occupants) ? (
                          <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-left">
                            <p className="text-sm text-amber-800 flex items-start gap-2">
                              <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                              Some occupants have incomplete information. You may still proceed with move-outs.
                            </p>
                          </div>
                        ) : null}
                        <div className="mt-4 text-left">
                          <div className="mb-4">
                            <label className="block text-sm font-medium leading-6 text-gray-900">Move Out Date</label>
                            <input
                              type="date"
                              value={moveOutDate}
                              onChange={(e) => setMoveOutDate(e.target.value)}
                              className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        <button
                          type="button"
                          disabled={saving || (selectedApt ? hasMissingMandatoryMoveOutInfo(selectedApt.occupants) : false)}
                          className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 disabled:opacity-50"
                          onClick={submitToMoveOut}
                        >
                          {saving ? "Saving..." : "Confirm"}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                          onClick={() => setActionModalOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default ExtractedApartments;
